import React, { useState, useRef, useCallback } from "react";
import { TextField, Grid, Typography, Alert } from "@mui/material";
import SimpleDialog from "./SimpleDialog";
import { register } from "../../../services/authService";
import ReCaptcha, { ReCaptchaRef } from "./ReCaptcha";

// Declare global grecaptcha
declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface RegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onRegistrationSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({
  open,
  onClose,
  onRegistrationSuccess,
  onSwitchToLogin,
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCaptchaRef>(null);

  const validateForm = () => {
    const { username, email, password, confirmPassword } = formData;

    if (!username.trim()) {
      return "Username is required";
    }

    if (username.trim().length < 6 || username.trim().length > 15) {
      return "Username must be 6-15 characters long";
    }

    if (!/^[a-zA-Z0-9]+$/.test(username.trim())) {
      return "Username must contain only letters and numbers";
    }

    if (!email.trim()) {
      return "Email is required";
    }

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }

    if (!password) {
      return "Password is required";
    }

    if (password.length < 6 || password.length > 15) {
      return "Password must be 6-15 characters long";
    }

    if (!/^[a-zA-Z0-9@#$%^&*]+$/.test(password)) {
      return "Password must contain only letters, numbers, or special characters (@#$%^&*)";
    }

    if (password !== confirmPassword) {
      return "Passwords do not match";
    }

    // reCAPTCHA is optional for now - we'll use a default value if not completed
    // if (!captchaToken) {
    //   return "Please complete the reCAPTCHA verification";
    // }

    return null;
  };

  const handleRegister = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await register(
        formData.username,
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        captchaToken || "splash-app-registration", // Use captcha token or simple bypass
      );

      setSuccess(
        "Registration successful! Please check your email to confirm your account, then you can log in.",
      );

      // Clear form
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
      });

      if (onRegistrationSuccess) {
        onRegistrationSuccess();
      }

      // Auto-close after 3 seconds and switch to login
      setTimeout(() => {
        handleClose();
        if (onSwitchToLogin) {
          onSwitchToLogin();
        }
      }, 3000);
    } catch (err: any) {
      console.error("Registration error:", err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status === 400) {
        setError(
          "Registration failed. Please check your information and try again.",
        );
      } else {
        setError(
          "Registration failed. Please check your connection and try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
    });
    setError(null);
    setSuccess(null);
    setIsLoading(false);
    setCaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  const handleInputChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
      // Clear error when user starts typing
      if (error) setError(null);
    };

  // Stable callback functions to prevent reCAPTCHA re-renders
  const handleCaptchaVerify = useCallback((token: string) => {
    setCaptchaToken(token);
    setError(null); // Clear any captcha-related errors
  }, []);

  const handleCaptchaExpired = useCallback(() => {
    setCaptchaToken(null);
    // Don't show error for optional captcha
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
    // Don't show error for optional captcha
  }, []);

  const registrationContent = () => (
    <Grid container spacing={2} style={{ marginTop: "2px", minWidth: "350px" }}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}

      {success && (
        <Grid item xs={12}>
          <Alert severity="success">{success}</Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="body1" style={{ marginBottom: "8px" }}>
          Create an account to save your artwork and join the ArtThread
          community.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          autoFocus
          fullWidth
          label="Username *"
          type="text"
          value={formData.username}
          onChange={handleInputChange("username")}
          disabled={isLoading || !!success}
          helperText="6-15 characters, letters and numbers only"
          size="small"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email *"
          type="email"
          value={formData.email}
          onChange={handleInputChange("email")}
          disabled={isLoading || !!success}
          size="small"
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          label="First Name"
          type="text"
          value={formData.firstName}
          onChange={handleInputChange("firstName")}
          disabled={isLoading || !!success}
          size="small"
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Last Name"
          type="text"
          value={formData.lastName}
          onChange={handleInputChange("lastName")}
          disabled={isLoading || !!success}
          size="small"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Password *"
          type="password"
          value={formData.password}
          onChange={handleInputChange("password")}
          disabled={isLoading || !!success}
          helperText="6-15 characters, letters, numbers, or @#$%^&*"
          size="small"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Confirm Password *"
          type="password"
          value={formData.confirmPassword}
          onChange={handleInputChange("confirmPassword")}
          disabled={isLoading || !!success}
          size="small"
          onKeyPress={(e) => {
            if (e.key === "Enter" && !isLoading && !success) {
              handleRegister();
            }
          }}
        />
      </Grid>

      {!success && (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "16px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Typography
              variant="body2"
              style={{ fontSize: "0.8em", color: "#666", marginBottom: "8px" }}
            >
              reCAPTCHA verification (optional)
            </Typography>
            <ReCaptcha
              ref={recaptchaRef}
              onVerify={handleCaptchaVerify}
              onExpired={handleCaptchaExpired}
              onError={handleCaptchaError}
            />
          </div>
        </Grid>
      )}

      {!success && (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            style={{ fontSize: "0.9em", color: "#666" }}
          >
            Already have an account?{" "}
            <button
              onClick={onSwitchToLogin}
              style={{
                color: "#1976d2",
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontSize: "inherit",
              }}
              disabled={isLoading}
            >
              Log in here
            </button>
          </Typography>
        </Grid>
      )}
    </Grid>
  );

  return (
    <SimpleDialog
      open={open}
      title="Create Your Account"
      content={registrationContent()}
      actionTitle={success ? "Close" : "Create Account"}
      actionCB={success ? handleClose : handleRegister}
      actionUseLoading={!success}
      actionIsLoading={isLoading}
      handleClose={handleClose}
      disableClose={isLoading}
    />
  );
};

export default RegistrationModal;
